import {
  Form,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconButton,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Input,
} from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoQuestionChoice } from "@/shared/model/edo-question-choice.model";
import { IEdoQuestion, defaultValue } from "@/shared/model/edo-question.model";
import { IEdoQuiz } from "@/shared/model/edo-quiz.model";
import { EdoQuestionTypeEnum } from "@/shared/model/enumerations/edo-question-type.mode";
import {
  addQuestionToQuiz,
  createEntity,
  updateEntity,
  updateQuestionChoices,
} from "@/shared/reducers/entities/edo-question.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { Plus, Check, X } from "lucide-react";
import axios, { AxiosResponse } from "axios";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Translate, translate } from "react-jhipster";
import { NumericFormat } from "react-number-format";
import { z } from "zod";
import { cn } from "@/shared/util/cn";

interface IEditQuestionProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: (question: IEdoQuestion) => void;
    question?: IEdoQuestion;
    quiz?: IEdoQuiz;
    type?: EdoQuestionTypeEnum;
}

function EditQuestionTypeField({
  onChange,
  typeReadOnly,
}: {
  value?: EdoQuestionTypeEnum;
  onChange: (type: EdoQuestionTypeEnum) => void;
  typeReadOnly?: boolean;
}) {
  const { control } = useFormContext();
  return (
    <Controller
      name="type"
      control={control}
      render={({ field }) => (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-[var(--color-mockup-text-mid)]">
            {translate("edocendoApp.edoQuestion.type")}
          </label>
          <Select
            value={field.value || ""}
            disabled={typeReadOnly}
            onValueChange={(v) => {
              if (typeReadOnly) {
                return;
              }
              const val = v as EdoQuestionTypeEnum;
              field.onChange(val);
              onChange(val);
            }}
          >
            <SelectTrigger className={typeReadOnly ? "cursor-not-allowed opacity-80" : undefined}>
              <SelectValue placeholder={translate("edocendoApp.edoQuestion.type")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={EdoQuestionTypeEnum.FREE_TEXT}>
                <Translate contentKey={`edocendoApp.EdoQuestionType.${EdoQuestionTypeEnum.FREE_TEXT}`} />
              </SelectItem>
              <SelectItem value={EdoQuestionTypeEnum.TRUE_FALSE}>
                <Translate contentKey={`edocendoApp.EdoQuestionType.${EdoQuestionTypeEnum.TRUE_FALSE}`} />
              </SelectItem>
              <SelectItem value={EdoQuestionTypeEnum.SINGLE_CHOICE}>
                <Translate contentKey={`edocendoApp.EdoQuestionType.${EdoQuestionTypeEnum.SINGLE_CHOICE}`} />
              </SelectItem>
              <SelectItem value={EdoQuestionTypeEnum.MULTIPLE_CHOICE}>
                <Translate contentKey={`edocendoApp.EdoQuestionType.${EdoQuestionTypeEnum.MULTIPLE_CHOICE}`} />
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    />
  );
}

function EditQuestionDescriptionField({
  onValueChange,
}: {
  onValueChange?: (value: string) => void;
}) {
  const { control } = useFormContext();
  return (
    <Controller
      name="description"
      control={control}
      render={({ field, fieldState }) => (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-[var(--color-mockup-text-mid)]">
            {translate("edocendoApp.edoQuestion.description")}
          </label>
          <textarea
            {...field}
            rows={4}
            className={cn(
              "flex w-full rounded-md border border-[var(--color-mockup-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]",
              fieldState.error && "border-red-500"
            )}
            value={field.value || ""}
            onChange={(e) => {
              const v = e.target.value;
              field.onChange(v);
              onValueChange?.(v);
            }}
          />
          {fieldState.error?.message && (
            <p className="text-sm text-red-600">{fieldState.error.message}</p>
          )}
        </div>
      )}
    />
  );
}

function EditQuestionExplanationCheckbox({
  onChange,
}: {
  checked?: boolean;
  onChange: (v: boolean) => void;
}) {
  const { control } = useFormContext();
  return (
    <Controller
      name="explanationRequired"
      control={control}
      render={({ field }) => (
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={!!field.value}
            onChange={(e) => {
              field.onChange(e.target.checked);
              onChange(e.target.checked);
            }}
            className="h-4 w-4 rounded border-[var(--color-mockup-border)] text-primary focus:ring-primary"
          />
          <span className="text-sm text-[var(--color-mockup-text-mid)]">
            (Se selezionato, lo studente dovrà scrivere la motivazione della risposta)
          </span>
        </label>
      )}
    />
  );
}

const EditQuestion = (props: IEditQuestionProps) => {
    const dispatch = useAppDispatch();
    const { loading, updating, errorMessage, updateSuccess, entity } = useAppSelector<
        EntityState<IEdoQuestion>
    >((state) => state.edoQuestion);

    const { question, open, onClose: handleClose, onSuccess: handleSuccess, type, quiz } = props;

    const initialType = question?.type ?? type ?? EdoQuestionTypeEnum.FREE_TEXT;
    const [form, setForm] = useState<IEdoQuestion>({
        ...defaultValue,
        type: initialType,
        ...(question ?? {}),
    });
    const [questionChoices, setQuestionChoices] = useState<IEdoQuestionChoice[]>([]);

    const isNew = form?.id === null || form?.id === undefined;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const change = {
            ...form,
            [event.target.name]:
                event.target.type === "checkbox"
                    ? event.target.checked
                    : event.target.value,
        };
        setForm(change);
    };

    useEffect(() => {
        if (!open) {
            return;
        }
        if (question?.id) {
            setForm({ ...defaultValue, ...question });
            getAllChoices(question.id);
        } else {
            setForm({
                ...defaultValue,
                type: type ?? EdoQuestionTypeEnum.FREE_TEXT,
                description: "",
            });
            setQuestionChoices([]);
        }
    }, [open, question, type]);

    useEffect(() => {
        if (open && updateSuccess) {
            if (entity) {
                if (!isNew) {
                    getAllChoices(form?.id);
                }
                setForm({ ...entity });
            }
        }
    }, [updateSuccess]);

    useEffect(() => {
        if (form.type === EdoQuestionTypeEnum.TRUE_FALSE) {
            setQuestionChoices([{
                id: null,
                description: `Vero`,
                isCorrect: true,
                score: 1,
            }, {
                id: null,
                description: `Falso`,
                isCorrect: false,
                score: 0,
            }]);
        } else if (form.type === EdoQuestionTypeEnum.FREE_TEXT) {
            setQuestionChoices([]);
        }
    }, [form.type]);

    const confirmSuccess = (data?: { type?: EdoQuestionTypeEnum; description?: string; explanationRequired?: boolean }) => {
        const payload = data ? { ...form, ...data } : form;
        if (isNew) {
            if (quiz) {
                dispatch(addQuestionToQuiz({ quizId: quiz.id, question: payload })).then((response) => {
                    const resPayload = response.payload as AxiosResponse<{ data: IEdoQuestion }>;
                    dispatch(updateQuestionChoices({ question: resPayload.data.data, choices: questionChoices }));
                    handleClose();
                    handleSuccess(resPayload.data.data);
                });
            } else {
                dispatch(createEntity({ ...payload })).then((response) => {
                    const resPayload = response.payload as AxiosResponse<{ data: IEdoQuestion }>;
                    dispatch(updateQuestionChoices({ question: resPayload.data.data, choices: questionChoices }));
                    handleClose();
                    handleSuccess(resPayload.data.data);
                });
            }
        } else {
            dispatch(updateEntity({ ...payload })).then(() => {
                dispatch(updateQuestionChoices({ question: form, choices: questionChoices }));
                handleClose();
                handleSuccess(payload);
            });
        }
    };

    const questionSchema = z.object({
        type: z.nativeEnum(EdoQuestionTypeEnum),
        description: z.string().min(1, "Required"),
        explanationRequired: z.boolean().optional(),
    });


    const getAllChoices = async (id: number) => {
        const response = await axios.get<{ data: IEdoQuestionChoice[] }>(`/questions/${id}/choices`);
        setQuestionChoices(response.data.data);
    };

    const handleAddChoice = () => {
        if (!form.type || !String(form.description ?? "").trim()) {
            return;
        }
        if (form.type === EdoQuestionTypeEnum.TRUE_FALSE && questionChoices.length > 1) {
            return;
        }
        if (isNew) {
            if (quiz) {
                dispatch(addQuestionToQuiz({ quizId: quiz.id, question: form }));
            } else {
                dispatch(createEntity(form));
            }
        }
        let score = 0;
        if ([EdoQuestionTypeEnum.TRUE_FALSE, EdoQuestionTypeEnum.SINGLE_CHOICE].includes(form.type)) {
            score = 0;
        } else {
            score = 1 / (questionChoices.length + 1);
        }

        const choice: IEdoQuestionChoice = {
            id: null,
            description: `Opzione ${questionChoices.length + 1}`,
            isCorrect: false,
            score,
        };
        setQuestionChoices([...questionChoices, choice]);
    };

    const handleEditChoice = (index: number, choice: IEdoQuestionChoice) => {
        const questionChoicesCopy = Array.from(questionChoices);
        questionChoicesCopy[index] = choice;
        setQuestionChoices(questionChoicesCopy);
    };

    const handleCheckCorrectChoices = async (index: number, choice: IEdoQuestionChoice) => {
        if (form.type === EdoQuestionTypeEnum.SINGLE_CHOICE || form.type === EdoQuestionTypeEnum.TRUE_FALSE) {
            const questionChoicesCopy = Array.from(questionChoices);
            questionChoicesCopy.map((c, i) => {
                if (i === index) {
                    c.isCorrect = true;
                } else {
                    c.isCorrect = false;
                }
                return choice;
            });
            setQuestionChoices(questionChoicesCopy);
        } else {
            const questionChoicesCopy = Array.from(questionChoices);
            questionChoicesCopy[index] = { ...questionChoicesCopy[index], isCorrect: true };
            setQuestionChoices(questionChoicesCopy);
        }
    };

    const handleUncheckChoice = async (index: number, choice: IEdoQuestionChoice) => {
        const questionChoicesCopy = Array.from(questionChoices);
        questionChoicesCopy[index] = { ...questionChoicesCopy[index], isCorrect: false };
        setQuestionChoices(questionChoicesCopy);
    };

    const handleRemoveChoice = async (index: number, choice: IEdoQuestionChoice) => {
        const questionChoicesCopy = Array.from(questionChoices);
        questionChoicesCopy.splice(index, 1);
        setQuestionChoices(questionChoicesCopy);
    };

    const getTotalScore = () => {
        if (form.type === EdoQuestionTypeEnum.SINGLE_CHOICE || form.type === EdoQuestionTypeEnum.TRUE_FALSE || form.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE || form.type === EdoQuestionTypeEnum.FILL_BLANK) {
            return questionChoices.reduce((acc, curr) => acc + (curr.isCorrect && curr.score > 0 ? curr.score : 0), 0);
        } else {
            return form.totalScore;
        }
    };

    const totalScore = useMemo(() => getTotalScore(), [questionChoices, form.type, form.totalScore]);

    useEffect(() => {
        if (!open) {
            setForm({ ...defaultValue });
            setQuestionChoices([]);
        }
    }, [open]);

    const getRadioChoiceOptions = () => (
        <fieldset
          className="space-y-2"
        >
          <legend className="mb-1 text-sm font-medium text-[var(--color-mockup-text-mid)]">
            Risposte disponibili
          </legend>
          <div className="space-y-2">
            {questionChoices.map((choice, index) => (
              <label
                key={index}
                className="flex cursor-pointer items-center gap-2"
              >
                <input
                  type="radio"
                  name="available-choices"
                  checked={choice.isCorrect}
                  onChange={() =>
                    choice.isCorrect
                      ? handleUncheckChoice(index, choice)
                      : handleCheckCorrectChoices(index, choice)
                  }
                  className="h-4 w-4 text-primary focus:ring-primary"
                />
                <input
                  className="min-w-0 flex-1 rounded border border-[var(--color-mockup-border)] bg-white px-2 py-1 text-sm dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]"
                  value={choice.description || ""}
                  onChange={(e) =>
                    handleEditChoice(index, {
                      ...choice,
                      description: e.target.value,
                    })
                  }
                  disabled={form.type === EdoQuestionTypeEnum.TRUE_FALSE}
                />
                <NumericFormat
                  onValueChange={(values) =>
                    handleEditChoice(index, {
                      ...choice,
                      score: values.floatValue,
                    })
                  }
                  placeholder={translate("edocendoApp.edoQuestionChoice.score")}
                  value={choice.score}
                  customInput={Input}
                  allowNegative
                  decimalSeparator=","
                  thousandSeparator="."
                  decimalScale={2}
                  className="w-20"
                  disabled={form.type !== EdoQuestionTypeEnum.TRUE_FALSE && isNew}
                />
                {form.type !== EdoQuestionTypeEnum.TRUE_FALSE && (
                  <IconButton
                    size="sm"
                    onClick={() => handleRemoveChoice(index, choice)}
                    aria-label="Rimuovi"
                  >
                    <X className="h-4 w-4" />
                  </IconButton>
                )}
              </label>
            ))}
          </div>
        </fieldset>
      );

    const getCheckboxChoiceOptions = () => (
        <fieldset
          className="mt-2 space-y-2"
        >
          <legend className="mb-1 text-sm font-medium text-[var(--color-mockup-text-mid)]">
            Risposte disponibili
          </legend>
          <div className="space-y-2">
            {questionChoices.map((choice, index) => (
              <label
                key={index}
                className="flex cursor-pointer items-center gap-2"
              >
                <input
                  type="checkbox"
                  checked={choice.isCorrect}
                  onChange={() =>
                    choice.isCorrect
                      ? handleUncheckChoice(index, choice)
                      : handleCheckCorrectChoices(index, choice)
                  }
                  className="h-4 w-4 rounded border-[var(--color-mockup-border)] text-primary focus:ring-primary"
                />
                <input
                  className="min-w-0 flex-1 rounded border border-[var(--color-mockup-border)] bg-white px-2 py-1 text-sm dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]"
                  value={choice.description || ""}
                  onChange={(e) =>
                    handleEditChoice(index, {
                      ...choice,
                      description: e.target.value,
                    })
                  }
                />
                <NumericFormat
                  onValueChange={(values) =>
                    handleEditChoice(index, {
                      ...choice,
                      score: values.floatValue,
                    })
                  }
                  placeholder={translate("edocendoApp.edoQuestionChoice.score")}
                  value={choice.score}
                  customInput={Input}
                  allowNegative
                  decimalSeparator=","
                  thousandSeparator="."
                  decimalScale={2}
                  className="w-20"
                />
                <IconButton
                  size="sm"
                  onClick={() => handleRemoveChoice(index, choice)}
                  aria-label="Rimuovi"
                >
                  <X className="h-4 w-4" />
                </IconButton>
              </label>
            ))}
          </div>
        </fieldset>
      );

    const getMultipleChoiceOptions = () => {
        if (form.type === EdoQuestionTypeEnum.SINGLE_CHOICE || form.type === EdoQuestionTypeEnum.TRUE_FALSE) {
            return getRadioChoiceOptions();
        } else {
            return getCheckboxChoiceOptions();
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
            <DialogContent
                showClose={false}
                className="flex max-h-[95vh] w-full max-w-2xl flex-col gap-0 overflow-hidden p-0"
            >
                <DialogHeader className="flex shrink-0 flex-row items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] px-6 py-4 dark:border-[var(--color-mockup-border)]">
                    <DialogTitle>
                        {isNew ? translate("edocendoApp.edoQuestion.home.createLabel") : translate("edocendoApp.edoQuestion.home.editLabel")}
                    </DialogTitle>
                    <div className="flex items-center gap-1">
                        <IconButton
                            disabled={updating}
                            onClick={handleClose}
                            aria-label="Chiudi"
                        >
                            <X className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                            disabled={updating}
                            type="submit"
                            form="edit-question-form"
                            aria-label="Salva"
                        >
                            <Check className="h-4 w-4" />
                        </IconButton>
                    </div>
                </DialogHeader>
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                    {!form ? (
                        <div className="flex justify-center py-4">
                            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        </div>
                    ) : (
                        <Form
                            key={`${question?.id ?? "new"}-${type ?? "any"}`}
                            id="edit-question-form"
                            schema={questionSchema}
                            onSubmit={(data) => confirmSuccess(data)}
                            defaultValues={{
                                type:
                                    question?.type ||
                                    type ||
                                    form.type ||
                                    EdoQuestionTypeEnum.FREE_TEXT,
                                description: question?.description || form.description || "",
                                explanationRequired:
                                    question?.explanationRequired ?? form.explanationRequired ?? false,
                            }}
                            className="flex flex-col gap-4"
                        >
                            <EditQuestionTypeField
                                value={form.type}
                                typeReadOnly={!isNew}
                                onChange={(t) => setForm((prev) => ({ ...prev, type: t }))}
                            />
                            <EditQuestionDescriptionField
                                onValueChange={(v) =>
                                    setForm((prev) => ({ ...prev, description: v }))
                                }
                            />
                            <fieldset className="space-y-2">
                                <legend className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                                    Motivare la risposta
                                </legend>
                                <EditQuestionExplanationCheckbox
                                    checked={form.explanationRequired}
                                    onChange={(v) => setForm((prev) => ({ ...prev, explanationRequired: v }))}
                                />
                            </fieldset>
                            <hr className="border-[var(--color-mockup-border)]" />
                            {(form.type === EdoQuestionTypeEnum.SINGLE_CHOICE || form.type === EdoQuestionTypeEnum.TRUE_FALSE || form.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE || form.type === EdoQuestionTypeEnum.FILL_BLANK) && (
                                <div className="space-y-2">
                                    <div className={questionChoices.length > 0 ? "mb-2" : ""}>
                                        {getMultipleChoiceOptions()}
                                    </div>
                                    {(form.type === EdoQuestionTypeEnum.SINGLE_CHOICE || form.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE || (form.type === EdoQuestionTypeEnum.TRUE_FALSE && questionChoices.length < 2)) && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={handleAddChoice}
                                            disabled={!form.type || !String(form.description ?? "").trim()}
                                            className="text-sm"
                                        >
                                            <Plus className="mr-2 h-4 w-4" />
                                            Aggiungi risposta
                                        </Button>
                                    )}
                                    <p className="mt-2 text-sm text-[var(--color-mockup-text-mid)]">
                                        Fra le risposte, selezionare la spunta accanto alle risposte giuste.
                                    </p>
                                </div>
                            )}
                            <div className="mt-2 space-y-1">
                                <label className="block text-sm font-medium text-[var(--color-mockup-text-mid)]">
                                    Punteggio totale della domanda
                                </label>
                                <NumericFormat
                                    onValueChange={(values) => {
                                        setForm({ ...form, totalScore: values.floatValue });
                                    }}
                                    placeholder={translate("edocendoApp.edoQuestion.totalScore")}
                                    disabled={form.type === EdoQuestionTypeEnum.SINGLE_CHOICE || form.type === EdoQuestionTypeEnum.TRUE_FALSE || form.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE || form.type === EdoQuestionTypeEnum.FILL_BLANK}
                                    name="totalScore"
                                    value={totalScore}
                                    customInput={Input}
                                    allowNegative
                                    decimalSeparator=","
                                    thousandSeparator="."
                                    decimalScale={2}
                                    className="w-full"
                                />
                            </div>
                        </Form>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default EditQuestion;